import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { TopLimitDropdown } from "./TopProductsChartCard";
import { TotalSaleGroupByDropdown } from "./TotalSaleGroupedBarCard";
import DashboardChartCardKebabMenu from "./DashboardChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { customerTierMedalUrl, mapTopCustomersApiToModelPatch, mergeTopCustomersModel } from "./dashboardChartsModel";
import DashboardChartCardSkeleton from "./DashboardChartCardSkeleton";
import { _Api } from "../common/api/_call";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./TopCustomersChartCard.scss";
/** In-flight dedupe for dev StrictMode double-mount. */
const TOP_CUSTOMERS_IN_FLIGHT = new Map();

function formatAmount(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function initials(name) {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/** Customer tier medal (Gold / Silver / Platinum). */
function GroupBadge({ groupKey }) {
  const k = String(groupKey ?? "gold").toLowerCase();
  const label =
    k === "silver" ? "Silver" : k === "platinum" ? "Platinum" : "Gold";
  return (
    <img
      className="pos-top-customers__group-badge"
      src={customerTierMedalUrl(k)}
      alt={label}
      draggable={false}
    />
  );
}

/**
 * @param {object} props
 * @param {Date | null} [props.startDate]
 * @param {Date | null} [props.endDate]
 * @param {() => void} [props.onMenuRemove]
 */
function TopCustomersChartCard({ startDate, endDate, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [topLimit, setTopLimit] = useState(10);
  const [groupBy, setGroupBy] = useState(() => "amount");
  /** `null` until first successful API response — then static demo or server rows. */
  const [apiPatch, setApiPatch] = useState(/** @type {Partial<import("./dashboardChartsModel.js").TopCustomersModel> | null} */ (null));
  const [fetchLoading, setFetchLoading] = useState(true);

  const merged = useMemo(() => mergeTopCustomersModel(apiPatch ?? null), [apiPatch]);

  useEffect(() => {
    let active = true;
    setFetchLoading(true);
    const fetchTopCustomers = async () => {
      try {
        if (!startDate) return;
        const payload = {
          start_date: moment(startDate).format("YYYY-MM-DD"),
          end_date: moment(endDate ?? startDate).format("YYYY-MM-DD"),
          limit: String(topLimit),
        };
        const reqKey = `${payload.start_date}|${payload.end_date}|${payload.limit}`;
        const existing = TOP_CUSTOMERS_IN_FLIGHT.get(reqKey);
        const req =
          existing ??
          _Api(payload, "api/v1/POS/dashboard/top-customers").finally(() => {
            TOP_CUSTOMERS_IN_FLIGHT.delete(reqKey);
          });
        if (!existing) {
          TOP_CUSTOMERS_IN_FLIGHT.set(reqKey, req);
        }
        const res = await req;
        if (!active) return;
        if (res?.code === 200) {
          setApiPatch(mapTopCustomersApiToModelPatch(res));
        }
      } finally {
        if (active) setFetchLoading(false);
      }
    };
    fetchTopCustomers();
    return () => {
      active = false;
    };
  }, [startDate, endDate, topLimit]);

  const baseRows = useMemo(() => {
    const list = merged.rows.map((r) => ({ ...r }));
    const mul = groupBy === "quantity" ? (a, b) => b.qty - a.qty : (a, b) => b.amount - a.amount;
    list.sort(mul);
    if (apiPatch == null) return list.slice(0, topLimit);
    return list;
  }, [merged.rows, groupBy, topLimit, apiPatch]);

  const [sort, setSort] = useState(() => ({
    key: /** @type {"customer"|"group"|"invoice"|"qty"|"amount"} */ ("amount"),
    dir: /** @type {"asc"|"desc"} */ ("desc"),
  }));

  useEffect(() => {
    setSort({ key: groupBy === "quantity" ? "qty" : "amount", dir: "desc" });
  }, [merged.rows, groupBy, topLimit]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const rows = useMemo(() => {
    const copy = baseRows.map((r) => ({ ...r }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case "customer":
          c = String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" });
          break;
        case "group":
          c = String(a.groupKey ?? "").localeCompare(String(b.groupKey ?? ""), undefined, { sensitivity: "base" });
          break;
        case "invoice":
          c = (Number(a.invoiceCount) || 0) - (Number(b.invoiceCount) || 0);
          break;
        case "qty":
          c = (Number(a.qty) || 0) - (Number(b.qty) || 0);
          break;
        case "amount":
          c = (Number(a.amount) || 0) - (Number(b.amount) || 0);
          break;
        default:
          c = 0;
      }
      return c * mul;
    });
    return copy;
  }, [baseRows, sort]);

  const thBase = "pos-top-customers-table__th";
  const thSorted = "pos-top-customers-table__th--sorted";

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("top-customers"));
  }, []);

  return (
    <div className="pos-dashboard-chart-card pos-dashboard-chart-card--top-customers">
      <header className="pos-dashboard-chart-card__header pos-dashboard-chart-card__header--with-controls pos-dashboard-chart-card__header--top-customers">
        <div className="pos-dashboard-chart-card__title-cluster">
          <span className="pos-dashboard-chart-card__title-prefix">{t("Top", { defaultValue: "Top" })}</span>
          <TopLimitDropdown value={topLimit} onChange={setTopLimit} />
          <h2 className="pos-dashboard-chart-card__title pos-dashboard-chart-card__title--inline">
            {t("Customers", { defaultValue: "Customers" })}
          </h2>
        </div>
        <div className="pos-dashboard-chart-card__header-actions">
          <TotalSaleGroupByDropdown value={groupBy === "quantity" ? "quantity" : "amount"} onChange={(v) => setGroupBy(v)} />
          <DashboardChartCardKebabMenu showViewToggle={false} onDownload={handleDownloadPng} onRemove={onMenuRemove} />
        </div>
      </header>
      <div className="pos-dashboard-chart-card__body pos-dashboard-chart-card__body--table pos-dashboard-chart-card__body--top-customers">
        <div ref={captureRef} className="pos-dashboard-chart-card__capture">
          {fetchLoading ? (
            <div className="pos-dashboard-chart-card__capture-skeleton">
              <DashboardChartCardSkeleton />
            </div>
          ) : (
          <div className="pos-top-customers-table" role="table" aria-label={t("Top customers", { defaultValue: "Top customers" })}>
            <div className="pos-top-customers-table__head">
              <div className="pos-top-customers-table__tr pos-top-customers-table__tr--head" role="row">
                <div className="pos-top-customers-table__th pos-top-customers-table__th--rank" role="columnheader">
                  #
                </div>
                <DashboardChartTableSortHeader
                  baseClassName={thBase}
                  sortedClassName={thSorted}
                  className="pos-top-customers-table__th--customer"
                  ariaSortLabel={t("Sort by customer", { defaultValue: "Sort by customer" })}
                  sorted={sort.key === "customer"}
                  direction={sort.dir}
                  onSort={() => toggleSort("customer")}
                >
                  {t("Customer", { defaultValue: "Customer" })}
                </DashboardChartTableSortHeader>
                <DashboardChartTableSortHeader
                  baseClassName={thBase}
                  sortedClassName={thSorted}
                  className="pos-top-customers-table__th--group"
                  ariaSortLabel={t("Sort by group", { defaultValue: "Sort by group" })}
                  sorted={sort.key === "group"}
                  direction={sort.dir}
                  onSort={() => toggleSort("group")}
                >
                  {t("Group", { defaultValue: "Group" })}
                </DashboardChartTableSortHeader>
                <DashboardChartTableSortHeader
                  baseClassName={thBase}
                  sortedClassName={thSorted}
                  className="pos-top-customers-table__th--num pos-top-customers-table__th--invoice"
                  ariaSortLabel={t("Sort by invoice count", { defaultValue: "Sort by invoice count" })}
                  sorted={sort.key === "invoice"}
                  direction={sort.dir}
                  onSort={() => toggleSort("invoice")}
                >
                  {t("Invoice", { defaultValue: "Invoice" })}
                </DashboardChartTableSortHeader>
                <DashboardChartTableSortHeader
                  baseClassName={thBase}
                  sortedClassName={thSorted}
                  className="pos-top-customers-table__th--num pos-top-customers-table__th--qty"
                  ariaSortLabel={t("Sort by quantity", { defaultValue: "Sort by quantity" })}
                  sorted={sort.key === "qty"}
                  direction={sort.dir}
                  onSort={() => toggleSort("qty")}
                >
                  {t("Qty", { defaultValue: "Qty" })}
                </DashboardChartTableSortHeader>
                <DashboardChartTableSortHeader
                  baseClassName={thBase}
                  sortedClassName={thSorted}
                  className="pos-top-customers-table__th--num pos-top-customers-table__th--amount pos-top-customers-table__th--last"
                  ariaSortLabel={t("Sort by amount", { defaultValue: "Sort by amount" })}
                  sorted={sort.key === "amount"}
                  direction={sort.dir}
                  onSort={() => toggleSort("amount")}
                >
                  {t("Amount", { defaultValue: "Amount" })}
                </DashboardChartTableSortHeader>
              </div>
            </div>
            <div className="pos-top-customers-table__scroll" role="rowgroup">
              {rows.map((row, i) => (
                <div key={`${row.email}-${i}`} className="pos-top-customers-table__tr" role="row">
                  <div className="pos-top-customers-table__td pos-top-customers-table__td--rank" role="cell">
                    {i + 1}
                  </div>
                  <div className="pos-top-customers-table__td pos-top-customers-table__td--customer" role="cell">
                    <span
                      className={`pos-top-customers__avatar ${row.avatarUrl ? "pos-top-customers__avatar--img" : ""}`}
                      style={row.avatarUrl ? { backgroundImage: `url(${row.avatarUrl})` } : undefined}
                    >
                      {!row.avatarUrl ? <span className="pos-top-customers__avatar-initials">{initials(row.name)}</span> : null}
                    </span>
                    <span className="pos-top-customers__identity">
                      <span className="pos-top-customers__name">{row.name}</span>
                      <span className="pos-top-customers__email">{row.email}</span>
                    </span>
                  </div>
                  <div className="pos-top-customers-table__td pos-top-customers-table__td--group" role="cell">
                    <GroupBadge groupKey={row.groupKey} />
                  </div>
                  <div className="pos-top-customers-table__td pos-top-customers-table__td--num pos-top-customers-table__td--invoice" role="cell">
                    {row.invoiceCount}
                  </div>
                  <div className="pos-top-customers-table__td pos-top-customers-table__td--num pos-top-customers-table__td--qty" role="cell">
                    {row.qty}
                  </div>
                  <div className="pos-top-customers-table__td pos-top-customers-table__td--num pos-top-customers-table__td--amount" role="cell">
                    {formatAmount(row.amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TopCustomersChartCard;
